// Minimal compatibility layer for authStateManager
// This provides basic functionality while we transition to the new auth system

import { supabase } from './supabaseClient';
import { AuthChangeEvent, Session, User } from '@supabase/supabase-js';

export class AuthStateManager {
  private static instance: AuthStateManager;
  public listeners: Set<(event: AuthChangeEvent, session: Session | null) => void> = new Set();
  private isInitialized = false;
  private lastSessionUserId: string | null = null; // Track last session to prevent duplicate SIGNED_IN events

  static getInstance(): AuthStateManager {
    if (!AuthStateManager.instance) {
      AuthStateManager.instance = new AuthStateManager();
    }
    return AuthStateManager.instance;
  }

  initialize(): void {
    if (this.isInitialized) {
      return;
    }
    
    // Set up auth listener ONLY ONCE with STRICT filtering
    supabase.auth.onAuthStateChange((event, session) => {
      // CRITICAL: Filter out problematic events at the SOURCE
      if (event === 'INITIAL_SESSION') {
        // Still track the session user ID for duplicate detection
        this.lastSessionUserId = session?.user?.id || null;
        return; // Don't notify listeners of INITIAL_SESSION events
      }
      
      if (event === 'TOKEN_REFRESHED') {
        return; // Don't notify listeners of TOKEN_REFRESHED events
      }
      
      // ADDITIONAL SAFEGUARD: Prevent duplicate SIGNED_IN events for the same user
      if (event === 'SIGNED_IN') {
        const currentUserId = session?.user?.id || null;
        if (this.lastSessionUserId === currentUserId && currentUserId !== null) {
          return; // Don't notify listeners of duplicate SIGNED_IN events
        }
        // Update tracked user ID
        this.lastSessionUserId = currentUserId;
      } else if (event === 'SIGNED_OUT') {
        // CRITICAL: Check if login is in progress - if so, ignore SIGNED_OUT event
        // This prevents logout events from interfering with login
        const loginInProgress = localStorage.getItem('enhancedLoginInProgress') === 'true';
        if (loginInProgress) {
          console.log('⚠️ SIGNED_OUT event received during login - ignoring (login in progress)');
          return; // Don't process logout during login
        }
        
        // Verify this is a real local logout (not cross-device or stale event)
        // Do this synchronously to avoid delays that interfere with login
        (async () => {
          try {
            // Small delay to let Supabase finish cleanup
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const { data: { session: localSession }, error: sessionError } = await supabase.auth.getSession();
            
            // If we still have a local session, this SIGNED_OUT event is likely from another device
            if (localSession && localSession.user) {
              console.log('⚠️ SIGNED_OUT event received but local session still exists - ignoring (likely cross-device)');
              return;
            }
            
            // If getSession() errored, the session might still be restoring (e.g. page refresh)
            // Retry once after a longer delay before concluding it's a real logout
            if (sessionError) {
              console.warn('⚠️ Error getting session during SIGNED_OUT verification - retrying...');
              await new Promise(resolve => setTimeout(resolve, 500));
              const { data: { session: retrySession }, error: retryError } = await supabase.auth.getSession();
              if (retrySession && retrySession.user) {
                console.log('⚠️ Session restored on retry - ignoring SIGNED_OUT event');
                return;
              }
              if (retryError) {
                console.warn('⚠️ Session still erroring on retry - ignoring SIGNED_OUT (session may still be restoring)');
                return;
              }
            }
            
            console.log('✅ SIGNED_OUT event confirmed - local session is gone');
            
            // Clear tracked user ID on confirmed local sign out
            this.lastSessionUserId = null;
            
            // Notify listeners of SIGNED_OUT event
            this.listeners.forEach(listener => {
              try {
                listener(event, session);
              } catch (error) {
                console.error('Error in auth state listener:', error);
              }
            });
          } catch (error) {
            console.warn('⚠️ Error verifying local logout:', error);
            // If we can't verify, only process if we're absolutely sure (session is null AND no login in progress)
            const loginInProgress = localStorage.getItem('enhancedLoginInProgress') === 'true';
            if (!loginInProgress && (!session || !session.user)) {
        this.lastSessionUserId = null;
              this.listeners.forEach(listener => {
                try {
                  listener(event, session);
                } catch (err) {
                  console.error('Error in auth state listener:', err);
                }
              });
            }
          }
        })();
        return; // Don't process synchronously - wait for async verification
      }
      
      // Only notify listeners of SIGNED_IN events (SIGNED_OUT handled above)
      if (event === 'SIGNED_IN') {
      this.listeners.forEach(listener => {
        try {
          listener(event, session);
        } catch (error) {
          console.error('Error in auth state listener:', error);
        }
      });
      }
    });

    this.isInitialized = true;
  }

  subscribe(callback: (event: AuthChangeEvent, session: Session | null) => void): () => void {
    this.listeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  async getSession(): Promise<{ session: Session | null; error: any }> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      return { session, error };
    } catch (error) {
      return { session: null, error };
    }
  }

  async getSessionInfo(): Promise<any> {
    const { session } = await this.getSession();
    return {
      hasSession: !!session,
      user: session?.user || null,
      expiresAt: session?.expires_at,
      accessToken: session?.access_token
    };
  }

  getListenerCount(): number {
    return this.listeners.size;
  }

  /**
   * Clear all tracked state - used during logout to ensure fresh login
   */
  clearState(): void {
    console.log('🧹 AuthStateManager: Clearing all tracked state for fresh login');
    this.lastSessionUserId = null;
  }

  static async initializeAll(): Promise<void> {
    const instance = AuthStateManager.getInstance();
    instance.initialize();
  }

  static async cleanupAll(): Promise<void> {
    const instance = AuthStateManager.getInstance();
    instance.listeners.clear();
  }
}

// Export singleton instance
export const authStateManager = AuthStateManager.getInstance();